'use client'

import * as React from 'react'
import { RadioGroup as RadixRadioGroup } from 'radix-ui'
import { Icon } from '../icon'
import { Text } from '../text'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { mtc, TypographyStyles } from '../../../utils/style-helpers'
import {
  iconStyles,
  indicatorStyles,
  itemStyles,
  rootStyles,
  itemContainerStyles,
  itemLabelStyles,
} from './styles'

export interface RadioGroupProps
  extends React.ComponentProps<typeof RadixRadioGroup.Root> {
  legend?: string
}

export const RadioGroup = ({
  className,
  legend,
  disabled,
  ...props
}: RadioGroupProps) => {
  return (
    <fieldset className={mtc(rootStyles(), className)}>
      {legend && <legend>{legend}</legend>}
      <RadixRadioGroup.Root {...props} disabled={disabled} />
    </fieldset>
  )
}

export const RadioGroupItem = ({
  className,
  label,
  id,
  ...props
}: React.ComponentProps<typeof RadixRadioGroup.Item> & { label?: string }) => {
  const isDisabled = props.disabled || false
  return (
    <div className={itemContainerStyles()}>
      <RadixRadioGroup.Item
        id={id}
        className={mtc(itemStyles(), className)}
        {...props}
      >
        <RadixRadioGroup.Indicator className={indicatorStyles()}>
          <Icon
            icon={solid('circle')}
            className={iconStyles()}
            color="neutral-700"
            variant="checkbox"
          />
        </RadixRadioGroup.Indicator>
      </RadixRadioGroup.Item>
      <Text
        className={mtc(itemLabelStyles({ disabled: isDisabled }))}
        variant={TypographyStyles.BODY_MD}
      >
        {label}
      </Text>
    </div>
  )
}

RadioGroup.displayName = RadixRadioGroup.Root.displayName
RadioGroupItem.displayName = RadixRadioGroup.Item.displayName

export const BooleanRadioGroup = ({
  value,
  onValueChange,
  yesLabel = 'Yes',
  noLabel = 'No',
  ...props
}: Omit<RadioGroupProps, 'value' | 'onValueChange'> & {
  value?: boolean
  onValueChange?: (value: boolean) => void
  yesLabel?: string
  noLabel?: string
}) => {
  return (
    <RadioGroup
      {...props}
      value={value?.toString()}
      onValueChange={(val) => onValueChange?.(val === 'true')}
    >
      <RadioGroupItem value="true" label={yesLabel} />
      <RadioGroupItem value="false" label={noLabel} />
    </RadioGroup>
  )
}
